<template>
  <div id="main-content" :key="isClaim ? 'claim' : 'benefitQuotes'">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
        <section>
          <template v-if="Object.values(apiStatus).every((bool) => bool === false) || isCRM">
            <template v-if="!isCRM">
              <p v-if="!isClaim" v-html="refData.content.benefitQuoteInformationText.informationText"></p>
              <p v-else v-html="refData.content.claimsInformationText.informationText"></p>
            </template>
          </template>
        </section>
      </div>
    </div>
    <Alert v-if="showTopLevelErrorMessage || apiStatus.fail || refDataFail" alertType="error" />
    <Alert v-if="getQuoteFail" alertType="error">
      <p>
        Sorry we weren't able to save your request. Please try submitting it again. If you've already done that please contact us on
        <a href="tel:1800 335 425">1800 335 425</a>
        . Our friendly team will be happy to help you.
      </p>
    </Alert>

    <Alert v-if="fileRemovedStatus" class="no-margin-bottom">
      <p>
        Sorry, the file you are trying to upload was lost. Please reselect your documents in
        <b v-if="!this.stepsInvalidStatus.step2">
          <button class="non-button-style" @click="updatePage(pages.SERVICE_INFORMATION.pageNumber)">Enter service information</button>
        </b>
        <b v-else>Enter service information</b>
        before continuing with your claim.
      </p>
    </Alert>
    <section id="multi-step-form" ref="multiStepForm" :class="{ 'last-step': currentPage === pages.REVIEW.pageNumber }">
      <!-- Form navigation -->
      <MultiStepFormNavigation :currentPage="currentPage" :links="navLinks" @linkClicked="updatePage" />

      <!-- Form contents -->
      <form @submit.prevent novalidate id="form-contents">
        <keep-alive>
          <component
            :is="currentStepComponent"
            @responseUpdated="updateVuexFormResponse"
            @apiFailUpdated="updateShowTopLevelErrorMessage"
            @getQuoteApiStatus="updateGetQuoteApiStatus"
            @crmNavigatePage="crmNavigatePage"
            @submit="updateSubmitStatus"
            @scrollToTopForm="scrollToTopForm"
            :isClaim="isClaim"
          />
        </keep-alive>
      </form>
    </section>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { mapGetters } from 'vuex';
import { getQueryString } from '@/utils/UrlUtils';
import { isBuildCRM } from '@/utils';
import { Alert, MultiStepFormNavigation } from '@/components';
import BenefitQuotesServices from '@/services/BenefitQuotes';
import ClaimsServices from '@/services/Claims';
import { ERROR_404_PAGE_NOT_FOUND, CONTACT_US } from '@/router/routeNames';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import GABQnMANCMixins from './GABQnMANCSteps/GABQnMANCMixins';
import { EnterServiceInformation, NewBenefitQuote, ReviewAndSubmit, SelectProvider, ProviderSearch, PaymentInformation } from './GABQnMANCSteps';

export default {
  name: 'GABQnMANC',

  mixins: [GABQnMANCMixins],

  components: {
    Alert,
    EnterServiceInformation,
    MultiStepFormNavigation,
    NewBenefitQuote,
    ReviewAndSubmit,
    SelectProvider,
    ProviderSearch,
    PaymentInformation,
  },

  data() {
    return {
      CONTACT_US,
      showTopLevelErrorMessage: false,
      getQuoteFail: false,
      refDataFail: false,
      allNavDisabled: false,
      isSubmit: false,
    };
  },

  computed: {
    ...mapGetters({
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),

    /**
     * Computes whether the link is disabled using the validation that is completed in Vuelidate.
     */
    navLinks() {
      let isStepOneDisabled = false;
      let step1Invalid = this.stepsInvalidStatus.step1;
      const step2Invalid = this.stepsInvalidStatus.step2;
      const step3Invalid = this.stepsInvalidStatus.step3;
      const step4Invalid = this.stepsInvalidStatus.step4;
      const step5Invalid = this.stepsInvalidStatus.step5;
      let isLastStepDisabled = [step1Invalid, step2Invalid, step3Invalid, step4Invalid];

      if (this.isClaim) {
        isLastStepDisabled.push(step5Invalid);
      }

      isLastStepDisabled = isLastStepDisabled.some(Boolean);
      if (this.allNavDisabled) {
        isStepOneDisabled = true;
        step1Invalid = true;
        isLastStepDisabled = true;
      }

      /**
       * The first link is always enabled unless current page is last step and get quote api is called. The consecutive links are enabled if the previous page is valid, if the input of the previous page is no longer valid, then all links besides the first one is disabled.
       * This may need to be modified as currently as soon as valid input is detected, it will allow the link to be enabled, for providers if the provider search items change, then the data for the selected provider will need to be removed to properly invalidate step 3.
       */
      const navs = [
        { linkTitle: !this.isClaim ? 'New benefit quote' : 'Member and claim type', pages: [this.pages.NEW_BENEFIT_QUOTE.pageNumber], disabled: isStepOneDisabled },
        { linkTitle: 'Select provider', pages: [this.pages.PROVIDER_SEARCH.pageNumber, this.pages.PROVIDER_SEARCH_RESULTS.pageNumber], disabled: step1Invalid },
        { linkTitle: 'Enter service information', pages: [this.pages.SERVICE_INFORMATION.pageNumber], disabled: step1Invalid || step2Invalid || step3Invalid },
        { linkTitle: 'Review and submit', pages: [this.pages.REVIEW.pageNumber], disabled: isLastStepDisabled },
      ];

      if (this.isClaim) {
        navs.splice(-1, 0, {
          linkTitle: 'Payment information',
          pages: [this.pages.PAYMENT_INFORMATION.pageNumber],
          disabled: step1Invalid || step2Invalid || step3Invalid || step4Invalid,
        });
      }

      return navs;
    },

    currentStepComponent() {
      switch (this.currentPage) {
        case this.pages.NEW_BENEFIT_QUOTE.pageNumber:
          return this.pages.NEW_BENEFIT_QUOTE.stepName;

        case this.pages.PROVIDER_SEARCH.pageNumber:
          return this.pages.PROVIDER_SEARCH.stepName;

        case this.pages.PROVIDER_SEARCH_RESULTS.pageNumber:
          return this.pages.PROVIDER_SEARCH_RESULTS.stepName;

        case this.pages.SERVICE_INFORMATION.pageNumber:
          return this.pages.SERVICE_INFORMATION.stepName;

        case this.pages.REVIEW.pageNumber:
          return this.pages.REVIEW.stepName;

        case this.pages.PAYMENT_INFORMATION.pageNumber:
          return this.pages.PAYMENT_INFORMATION.stepName;

        default:
          logger.log('GetABenefitQuote:: currentStepComponent() - Unexpected page number passed-in', this.currentPage, this.pages.PAYMENT_INFORMATION.pageNumber);
          return '';
      }
    },
  },

  watch: {
    currentPage() {
      this.showTopLevelErrorMessage = false;
      this.getQuoteFail = false;
    },

    isClaim(newVal) {
      this.clearAllDataFromApis();
      this.pageInitialisation();

      if (newVal) {
        this.pages.REVIEW.pageNumber = 6;
        this.pages.PAYMENT_INFORMATION.pageNumber = 5;
      } else {
        this.pages.REVIEW.pageNumber = 5;
        this.pages.PAYMENT_INFORMATION.pageNumber = null;
      }
    },
  },

  beforeMount() {
    let memberHasAccess = true;

    // if is not CRM build and current policy has no extras, member does not have access to this page
    if (!isBuildCRM() && !this.currentIsExtrasFlag) {
      memberHasAccess = false;
    }

    if (memberHasAccess) {
      this.pageInitialisation();
    } else {
      // if member has no access, redirect to member portal homepage
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }
    if (this.fileUploadStatus) {
      this.updateFileRemoved(true);
      this.updateStepInvalidStatus({ ...this.stepsInvalidStatus, step4: true });

      // if the current page is after 'Enter service information', then users are redirected back to this step
      // otherwise, reloading page should take users back to the same page
      if (this.currentPage >= this.pages.SERVICE_INFORMATION.pageNumber) {
        this.updatePage(this.pages.SERVICE_INFORMATION.pageNumber);
      }
    } else {
      this.updateFileRemoved(false);
    }
    // file upload will always be false when page is reloaded
    this.updateFileUpload(false);
  },

  mounted() {
    // Only listen to the pop state event if this is not running in CRM.
    if (!isBuildCRM()) {
      // This will add a listener to the window back and forward button, so that it can call a callback function to perform some operation when it is called.
      window.addEventListener('popstate', this.navigationCallback, false);
      window.addEventListener('beforeunload', this.preventLeavePage);
    } else {
      this.resetVuex();
    }
  },

  beforeRouteEnter(to, from, next) {
    next();
  },

  beforeRouteLeave(to, from, next) {
    if (!this.confirmLeave()) {
      // eslint-disable-next-line no-alert
      next(false);
    } else {
      next();
    }
  },

  beforeUnmount() {
    // Only remove the listener if not running in CRM mode.
    if (!isBuildCRM()) {
      // Remove the listener for the back and forward browser buttons.
      window.removeEventListener('popstate', this.navigationCallback);
      window.removeEventListener('beforeunload', this.preventLeavePage);
      this.clearAllDataFromApis();
    }
  },

  methods: {
    async pageInitialisation() {
      this.showTopLevelErrorMessage = false;
      this.getQuoteFail = false;
      this.refDataFail = false;
      this.allNavDisabled = false;
      this.apiStatus.fail = false;
      this.apiStatus.loading = true;

      // fetch general api if member has access
      try {
        this.resetRefGeneral(); // Clear existing GABQ multi-step form reference data before attempting the call again.
        const service = !this.isClaim ? BenefitQuotesServices : ClaimsServices;
        const res = await service.getMultiStepFormReferenceData();
        this.updateRefGeneral(res);
      } catch (error) {
        logger.log(error);
        this.apiStatus.fail = true;
        this.refDataFail = true;
      } finally {
        this.apiStatus.loading = false;
      }

      // get to correct step based on `step` query in url
      const step1PageNumber = this.pages.NEW_BENEFIT_QUOTE.pageNumber;
      let currentStep = Number(getQueryString('step'));
      if (currentStep) {
        const pageNumbs = [];
        Object.values(this.pages).forEach((pageObj) => {
          pageNumbs.push(pageObj.pageNumber);
        });
        currentStep = Number.isNaN(currentStep) ? step1PageNumber : Math.trunc(currentStep);

        // check if `currentStep` is within page number range. if not, update current page to step 1
        if (currentStep < Math.min(...pageNumbs) || currentStep > Math.max(...pageNumbs)) {
          currentStep = step1PageNumber;
        }

        this.updatePage(currentStep);
      } else {
        this.updatePage(step1PageNumber);
        this.$route.query.step = step1PageNumber;
      }

      // if case member visit this page by put url with specific valid `step` query, always navigate to step 1
      if (Object.values(this.stepsInvalidStatus).every(Boolean)) {
        this.updatePage(step1PageNumber);
        this.$route.query.step = step1PageNumber;
      }
    },

    updateGetQuoteApiStatus(data) {
      this.getQuoteFail = data.fail;
      this.allNavDisabled = data.apiInProgress;
    },

    updateSubmitStatus(data) {
      this.isSubmit = data;
    },

    navigationCallback() {
      this.updatePage(Number(this.$route.query.step));
    },

    updateShowTopLevelErrorMessage(data) {
      this.showTopLevelErrorMessage = data;
    },

    updateVuexFormResponse(data) {
      const pagesObjWithoutReview = cloneDeep(this.pages);
      delete pagesObjWithoutReview.REVIEW;
      const formResponseStepRange = Object.values(pagesObjWithoutReview).map((obj) => {
        return obj.pageNumber;
      });
      if (this.currentPage >= Math.min(...formResponseStepRange) && this.currentPage <= Math.max(...formResponseStepRange)) {
        const currentStep = `step${this.currentPage}`;
        const stepsInvalidStatus = this.stepsInvalidStatus;
        const formResponses = cloneDeep(this.feature);

        switch (this.currentPage) {
          case this.pages.NEW_BENEFIT_QUOTE.pageNumber:
            this.pages.NEW_BENEFIT_QUOTE.isInvalid = data.isInvalid;
            break;

          case this.pages.PROVIDER_SEARCH.pageNumber:
            this.pages.PROVIDER_SEARCH.isInvalid = data.isInvalid;
            break;

          case this.pages.PROVIDER_SEARCH_RESULTS.pageNumber:
            this.pages.PROVIDER_SEARCH_RESULTS.isInvalid = data.isInvalid;
            break;

          case this.pages.SERVICE_INFORMATION.pageNumber:
            this.pages.SERVICE_INFORMATION.isInvalid = data.isInvalid;
            break;

          default:
            break;
        }
        formResponses[currentStep] = data.response;
        stepsInvalidStatus[currentStep] = data.isInvalid;

        this.updateStepInvalidStatus(stepsInvalidStatus);
        this.updateFormResponse(formResponses);
      }
    },

    confirmLeave() {
      if (!this.fileUploadStatus || this.isSubmit) return true;
      // eslint-disable-next-line no-alert
      return window.confirm('Do you want to leave? Your unsaved changes will be lost.');
    },

    preventLeavePage(e) {
      // only stop refresh or close browser tab if user made some change without save
      if (this.fileUploadStatus) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    },

    scrollToTopForm() {
      // Support scrollTop variations in different browsers
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      // Get the calculated position of multistep form, added an offset of -15 for padding
      const top = this.$refs?.multiStepForm?.getBoundingClientRect().top + scrollTop - 15;
      // Scroll on top of the multistep form
      window.scrollTo(0, top);
    },
  },
};
</script>

<style lang="scss" scoped>
#multi-step-form {
  height: 100%;

  @include mobile {
    height: auto;
  }
}

.api-loading {
  min-height: 400px;
}

#form-contents {
  margin-bottom: 0;

  :deep(fieldset),
  :deep(#review-page) {
    .form-page-nav {
      section {
        @include mobile {
          grid-template-columns: 1fr 1fr;

          button {
            grid-row: 1 !important;

            &:not(.secondary) {
              grid-column: 2 !important;
            }
          }
        }
      }
    }
  }

  .in-step-api-loading-wrapper {
    @include grid-layout(null, spacer(1));
    @include grid-template(2fr 1fr);
    margin-bottom: spacer(2);

    .loading-wrapper {
      padding: spacer(5.5) spacer(4);
      margin-top: spacer(1);
    }
  }
  .radio-group {
    @include tablet-and-above {
      grid-template-columns: 1fr 1fr;
    }

    @include mobile {
      grid-template-columns: 1fr;
    }

    &.small {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    }
  }
}

.content-page-banner {
  margin-bottom: 16px;
}
</style>
